// Pure init helpers for MobileDALWizard.
// Extrait du wizard pour pouvoir tester la relecture des données mobile sans React.
// Bug source (P0 #2 audit AUDIT_DAL_MOBILE.md) : le wizard initialisait depuis
// `initialData[zone.id]` alors que les données mobile sont sous
// `initialData.zones_mobile[zone.id]`. Conséquence : visiteur repartait à zéro
// à la 2ᵉ session.

/**
 * Token d'authentification du wizard DAL — source UNIQUE pour les 2 contextes.
 *   - PWA mobile terrain  : `mobile_dal_token` (émis par /api/dal/mobile-auth).
 *   - Desktop (onglet DAL): `session_token` (session utilisateur classique).
 * Le backend `_verify_mobile_token` accepte les DEUX (CC-AUDIT-43 L2). On évite
 * ainsi de dupliquer la résolution du token dans chaque fetch du wizard.
 */
export function getDalAuthToken() {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("mobile_dal_token") || localStorage.getItem("session_token");
}

/**
 * Construit le state initial des zones depuis les données serveur.
 * Précédence : zones_mobile[id] > flat initialData[id] (back-compat) > defaults config.
 * @param {object} initialData — `infos_dal` retourné par /api/dal/mobile-auth
 * @param {Array}  zonesConfig — ZONES_CONFIG du wizard (id, fields, travaux)
 * @param {object} dossierLogement — pour pré-remplir certains champs (ex: largeur_couloir)
 */
export function buildInitialZonesData(initialData, zonesConfig, dossierLogement = {}) {
  const init = {};
  const zonesMobile = (initialData && initialData.zones_mobile) || {};
  const log = dossierLogement || {};

  for (const zone of zonesConfig) {
    const fromMobile = zonesMobile[zone.id];
    const fromFlat = initialData && initialData[zone.id];
    const persisted = fromMobile || fromFlat || null;
    const persistedFields = (persisted && persisted.fields) || {};

    // Pré-remplissage auto depuis logement (uniquement si pas saisi)
    const autoFilled = {};
    if (zone.id === "entree" && !persistedFields.largeur_couloir && log.largeur_couloir != null) {
      autoFilled.largeur_couloir = String(log.largeur_couloir);
    }

    const defaultTravaux = (zone.travaux || []).filter(t => t.default).map(t => t.id);

    init[zone.id] = {
      // enabled : respecter `false` explicite, sinon défaut true
      enabled: persisted && typeof persisted.enabled === "boolean" ? persisted.enabled : true,
      fields: { ...autoFilled, ...persistedFields },
      travaux: persisted && Array.isArray(persisted.travaux) ? persisted.travaux : defaultTravaux,
      photos: persisted && Array.isArray(persisted.photos) ? persisted.photos : [],
    };
  }

  return init;
}

/**
 * Besoins du bénéficiaire — précédence : initialData.besoins > clientInfo > defaults.
 */
export function buildInitialBesoins(initialData, clientInfo = {}) {
  const saved = (initialData && initialData.besoins) || {};
  const c = clientInfo || {};
  const pick = (key, fallback) => {
    if (saved[key] !== undefined && saved[key] !== "" && saved[key] !== null) return saved[key];
    if (c[key] !== undefined && c[key] !== "" && c[key] !== null) return c[key];
    return fallback;
  };
  return {
    age: pick("age", ""),
    mobilite: pick("mobilite", "autonome"),
    gir: pick("gir", ""),
    apa: pick("apa", false),
    rqth: pick("rqth", false),
    historique_chutes: pick("historique_chutes", ""),
    difficultes_quotidiennes: pick("difficultes_quotidiennes", ""),
    usage_reel: pick("usage_reel", ""),
    // `observations` = champ TOP-LEVEL de infos_dal (pas sous besoins) — c'est là
    // que le générateur PDF le lit (dal_sections.py). On l'hydrate donc depuis le
    // top-level et on le patchera sur le path "observations" (cf. MobileDALWizard).
    observations: (initialData && initialData.observations) || "",
  };
}

/**
 * Intervenant — précédence : initialData.intervenant (si nom rempli) > visiteurAmo prop > defaults.
 */
export function buildInitialIntervenant(initialData, visiteurAmo = "") {
  const saved = (initialData && initialData.intervenant) || {};
  if (saved.nom) {
    return {
      nom: saved.nom,
      prenom: saved.prenom || "",
      fonction: saved.fonction || "Visiteur AMO",
      telephone: saved.telephone || "",
    };
  }
  if (visiteurAmo) {
    return { nom: visiteurAmo, prenom: "", fonction: "Visiteur AMO", telephone: "" };
  }
  return { nom: "", prenom: "", fonction: "Visiteur AMO", telephone: "" };
}

// =============================================================================
// CC-AUDIT-43 L1 — config zones servie par le backend (source unique front/back)
// =============================================================================

/**
 * Fusionne la config zones SERVEUR (GET /api/dal/zones-config →
 * `{zone_id: {name, travaux:{id:label}}}`) avec la config LOCALE du wizard, qui
 * porte les métadonnées UI non sérialisables (icônes React, couleurs,
 * définitions de champs `fields`, flags `default`, `critical`).
 *
 * Le backend est AUTORITAIRE sur : présence + ordre des zones, IDs des travaux,
 * libellés (zone + travaux). Le local fournit le reste (icon, color, fields,
 * critical, defaults par travail). C'est cette partie qui ne peut PAS venir du
 * JSON (composants React) → fallback local indispensable.
 *
 * Si `apiZones` est vide / invalide → retourne `localConfig` inchangé (résilience
 * API KO / offline-first DAL Mobile).
 */
export function mergeZonesConfig(localConfig, apiZones) {
  if (!apiZones || typeof apiZones !== "object" || Array.isArray(apiZones)) {
    return localConfig;
  }
  const ids = Object.keys(apiZones);
  if (ids.length === 0) return localConfig;
  const localById = Object.fromEntries((localConfig || []).map(z => [z.id, z]));
  return ids.map(id => {
    const local = localById[id] || {};
    const apiZone = apiZones[id] || {};
    const localTravauxById = Object.fromEntries(
      (local.travaux || []).map(t => [t.id, t])
    );
    const apiTravaux =
      apiZone.travaux && typeof apiZone.travaux === "object"
        ? Object.entries(apiZone.travaux).map(([tid, label]) => ({
            id: tid,
            label,
            // conserve le flag `default` local si le travail existe localement
            default: localTravauxById[tid]?.default ?? false,
          }))
        : null;
    return {
      ...local, // icon, color, fields, critical (métadonnées UI locales)
      id,
      name: apiZone.name || local.name || id,
      travaux: apiTravaux && apiTravaux.length ? apiTravaux : local.travaux || [],
    };
  });
}

/**
 * Signature stable d'une config (IDs zones + IDs travaux, dans l'ordre). Sert à
 * détecter une DIVERGENCE réelle entre config serveur et fallback local : si
 * identiques, inutile de reconstruire zonesData/selectedZoneIds (cas normal =
 * zéro risque pour l'autosave en cours). Si différentes, le wizard se réaligne.
 */
export function zonesConfigSignature(config) {
  return (config || [])
    .map(z => `${z.id}:${(z.travaux || []).map(t => t.id).join(",")}`)
    .join("|");
}

// =============================================================================
// PATCH per-field — validation client mirror du backend dal_patch.py
// =============================================================================

const _BESOINS_FIELDS = new Set([
  "age", "mobilite", "gir", "apa", "rqth",
  "historique_chutes", "difficultes_quotidiennes",
  "usage_reel",   // DAL Phase 2 — miroir backend dal_patch._BESOINS_FIELDS
]);
const _INTERVENANT_FIELDS = new Set(["nom", "prenom", "fonction", "telephone"]);
// DAL Phase 2 — étape Infos (cf. backend dal_patch). Bénéficiaire/Logement =
// canoniques (write-through metier_clients / dossier.logement) ; Handicap +
// logement.zone_logement = spécifiques DAL (infos_dal).
const _BENEFICIAIRE_FIELDS = new Set(["date_naissance", "situation_familiale", "nb_personnes_foyer"]);
const _LOGEMENT_FIELDS = new Set(["type_logement", "statut_occupation", "annee_construction", "surface_habitable", "nb_pieces", "nb_niveaux", "zone_logement"]);
const _HANDICAP_FIELDS = new Set(["reconnaissance_handicap", "equipement_deplacement", "equipements_detail", "regime_retraite", "evaluation_ergotherapeute", "condition_cas"]);
const _ZONE_DIRECT = new Set(["enabled", "travaux", "description_forme"]);
const _ZONE_NESTED = new Set(["fields"]);
const _TOPLEVEL = new Set([
  "selected_zones",
  "date_visite_mobile",
  // BUG-05 : surface habitable saisissable depuis mobile si vide en DB.
  "infos_logement.surface_habitable",
  // DAL Phase 2 — observations générales (top-level infos_dal, cf. backend
  // dal_patch._TOPLEVEL_FIELDS). Lu par le PDF (dal_sections.py).
  "observations",
]);
const _FORBIDDEN = /[$\\]|\.\./;

/**
 * Mirror client-side du backend dal_patch.validate_patch_path. Permet d'éviter
 * un round-trip réseau pour des paths manifestement invalides. Renvoie boolean
 * (pas d'exception, pour que les onBlur restent silencieux).
 */
export function validatePatchPath(path, zoneIds) {
  if (!path || typeof path !== "string") return false;
  if (_FORBIDDEN.test(path)) return false;
  if (_TOPLEVEL.has(path)) return true;
  const parts = path.split(".");
  if (parts.some(p => p === "")) return false;
  const head = parts[0];
  if (head === "besoins") {
    return parts.length === 2 && _BESOINS_FIELDS.has(parts[1]);
  }
  if (head === "intervenant") {
    return parts.length === 2 && _INTERVENANT_FIELDS.has(parts[1]);
  }
  if (head === "beneficiaire") {
    return parts.length === 2 && _BENEFICIAIRE_FIELDS.has(parts[1]);
  }
  if (head === "logement") {
    return parts.length === 2 && _LOGEMENT_FIELDS.has(parts[1]);
  }
  if (head === "handicap") {
    return parts.length === 2 && _HANDICAP_FIELDS.has(parts[1]);
  }
  if (head === "zones") {
    if (parts.length < 3) return false;
    const zoneId = parts[1];
    const set = zoneIds instanceof Set ? zoneIds : new Set(zoneIds || []);
    if (!set.has(zoneId)) return false;
    const sub = parts[2];
    if (_ZONE_DIRECT.has(sub)) return parts.length === 3;
    if (_ZONE_NESTED.has(sub)) return parts.length >= 4;
    return false;
  }
  return false;
}

// =============================================================================
// P2 — sélection des zones
// =============================================================================

/**
 * Retourne la liste des zone_id à évaluer, dans l'ordre canonique de zonesConfig.
 * Précédence :
 *   1. infosDal.selected_zones si défini (filtrée pour ignorer les zones inconnues)
 *   2. zones_mobile : toutes les zones avec enabled !== false
 *   3. defaults : toutes les zones de zonesConfig
 */
export function getSelectedZoneIds(infosDal, zonesConfig) {
  const knownIds = (zonesConfig || []).map(z => z.id);
  const knownSet = new Set(knownIds);

  if (Array.isArray(infosDal?.selected_zones)) {
    const selected = new Set(infosDal.selected_zones.filter(id => knownSet.has(id)));
    return knownIds.filter(id => selected.has(id));
  }

  const zm = infosDal?.zones_mobile || {};
  const hasZmSignal = Object.keys(zm).length > 0;
  if (hasZmSignal) {
    return knownIds.filter(id => {
      const z = zm[id];
      // enabled undefined → considéré comme coché (cas dossier antérieur à P2)
      return !z || z.enabled !== false;
    });
  }

  // Premier accès : aucune zone cochée par défaut — Jordan coche explicitement
  // les zones concernées par sa visite (cf. BUG-04). Tout coché par défaut était
  // trop long à décocher, et le visiteur sait quelles pièces sont touchées.
  return [];
}

/** Construit la liste des steps wizard : Besoins → Sélection → zones cochées →
 *  Solutions (mapping risques↔solutions, dérivé des zones) → Docs.
 *  L'étape « solutions » est placée APRÈS les zones (le mapping découle des
 *  préconisations cochées par zone) et AVANT « docs » / Terminer. */
export function buildStepList(selectedZoneIds) {
  // DAL Phase 2 — "infos" (Bénéficiaire/Logement/Situation handicap) ouvre le
  // wizard, avant Besoins (cf. DAL Excel : Infos = 1ʳᵉ étape).
  return ["infos", "besoins", "selection", ...(selectedZoneIds || []), "solutions", "docs"];
}

export function getNextStepKey(currentKey, steps) {
  const i = steps.indexOf(currentKey);
  if (i === -1) return steps[0];        // recovery : reset à Besoins
  if (i >= steps.length - 1) return currentKey;  // bout de liste
  return steps[i + 1];
}

export function getPrevStepKey(currentKey, steps) {
  const i = steps.indexOf(currentKey);
  if (i <= 0) return steps[0];          // bord ou inconnu : reste/retombe sur Besoins
  return steps[i - 1];
}

// =============================================================================
// BUG-01 — Bouton "Auto-remplir depuis zones" (DALForm desktop)
// =============================================================================

/**
 * Map la réponse de GET /api/metier/lookup/visiteurs vers la shape attendue
 * par les <select> du wizard mobile et du DALForm desktop.
 *
 * L'endpoint actuel renvoie un array de strings ("Prenom Nom"). On normalise en
 * `{id, nom, prenom, type}` pour que les deux UI partagent le même rendering.
 * Filtre les chaînes vides/blank et passe-through si on reçoit déjà des objets
 * (futurs-proof si l'API évolue).
 */
export function mapLookupVisiteursResponse(data) {
  if (!Array.isArray(data)) return [];
  const out = [];
  for (const item of data) {
    if (item && typeof item === "object") {
      out.push(item);
      continue;
    }
    if (typeof item !== "string") continue;
    const name = item.trim();
    if (!name) continue;
    out.push({ id: name, nom: name, prenom: "", type: "AMO", telephone: "" });
  }
  return out;
}

/**
 * Construit le mapping {probleme, solution, zone} depuis les zones évaluées.
 * Itère sur `preconisations` (source de vérité depuis le bridge mobile) et
 * appaire avec `problematiques` quand disponible. Génère une entrée par
 * préconisation, même si problematiques vide.
 *
 * Ancien bug : itération sur `problematiques` uniquement → 0 mappings quand
 * problematiques vide (cas systématique après bridge mobile).
 */
export function buildSolutionsMappingFromZones(zones) {
  const out = [];
  for (const z of zones || []) {
    if (z.etat !== "travaux_necessaires") continue;
    const precos = Array.isArray(z.preconisations) ? z.preconisations : [];
    const probs = Array.isArray(z.problematiques) ? z.problematiques : [];
    for (let i = 0; i < precos.length; i++) {
      out.push({
        probleme: probs[i] || "",
        solution: precos[i],
        zone: z.id || z.zone,
      });
    }
  }
  return out;
}

/**
 * Projette le state `zonesData` du wizard mobile (objet
 * `{[zoneId]: {enabled, fields, travaux, photos}}`) vers une liste de zones
 * « évaluées » au format attendu par {@link buildSolutionsMappingFromZones}
 * (`[{id, zone, etat, preconisations, problematiques}]`).
 *
 * Miroir CLIENT du backend `dal_bridge.project_zones_mobile_to_evaluees` :
 *   - zone absente / enabled === false      → ignorée (pas de préconisation),
 *   - enabled + travaux cochés              → etat="travaux_necessaires",
 *                                             preconisations = libellés des travaux,
 *   - enabled sans travaux                  → ignorée (etat "adapte", pas de mapping).
 *
 * Ne sert qu'à PRÉ-REMPLIR l'étape Solutions du wizard sans round-trip réseau.
 * La projection canonique (persistée en `zones_evaluees`) reste celle du backend.
 */
export function buildZonesEvalueesFromZonesData(zonesData, zonesConfig) {
  const out = [];
  const zd = zonesData || {};
  for (const zone of zonesConfig || []) {
    const z = zd[zone.id];
    if (!z || z.enabled === false) continue;
    const travauxIds = Array.isArray(z.travaux) ? z.travaux : [];
    if (travauxIds.length === 0) continue;
    const labelById = Object.fromEntries((zone.travaux || []).map(t => [t.id, t.label]));
    out.push({
      id: zone.id,
      zone: zone.id,
      etat: "travaux_necessaires",
      preconisations: travauxIds.map(t => labelById[t] || t),
      problematiques: [],
    });
  }
  return out;
}
